import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { 
  User, Mail, Lock, Shield, Calendar, Settings, Bell, Palette, 
  Eye, EyeOff, Check, AlertCircle, Loader2, ChevronRight, Sun, Moon, Monitor
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { NotificationRulesSection } from "@/components/NotificationRulesSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProfileSettings = () => {
  const { user, token, refreshUser } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  
  // Profile state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState("");
  
  // Password state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    theme: "light",
    notifications_enabled: true,
    email_notifications: true,
    compact_view: false
  });
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSuccess, setPrefsSuccess] = useState(false);

  // Load user data
  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  // Load preferences
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const res = await axios.get(`${API}/auth/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setPreferences(res.data);
      } catch (err) {
        console.error("Failed to load preferences:", err);
      }
    };
    if (token) loadPreferences();
  }, [token]);

  // Update profile
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileError("");
    setProfileSuccess(false);
    
    try {
      await axios.patch(`${API}/auth/profile`, { name, email }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileSuccess(true);
      if (refreshUser) refreshUser();
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (err) {
      setProfileError(err.response?.data?.detail || "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  // Change password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setPasswordError("");
    setPasswordSuccess(false);
    
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      setPasswordLoading(false);
      return;
    }
    
    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      setPasswordLoading(false);
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPasswordSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setTimeout(() => setPasswordSuccess(false), 3000);
    } catch (err) {
      setPasswordError(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Update preferences
  const handleUpdatePreferences = async (key, value) => {
    const newPrefs = { ...preferences, [key]: value };
    setPreferences(newPrefs);
    setPrefsLoading(true);
    
    // Handle theme change locally
    if (key === "theme") {
      if (value === "dark" && !isDark) toggleTheme();
      if (value === "light" && isDark) toggleTheme();
      if (value === "system") {
        const systemDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
        if (systemDark !== isDark) toggleTheme();
      }
    }
    
    try {
      await axios.put(`${API}/auth/preferences`, newPrefs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrefsSuccess(true);
      setTimeout(() => setPrefsSuccess(false), 2000);
    } catch (err) {
      console.error("Failed to save preferences:", err);
    } finally {
      setPrefsLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0";
      case "product_manager": return "bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0";
      case "team_member_business": return "bg-gradient-to-r from-teal-500 to-teal-600 text-white border-0";
      case "team_member_it": return "bg-gradient-to-r from-amber-500 to-amber-600 text-white border-0";
      default: return "bg-slate-500 text-white border-0";
    }
  };

  const formatRole = (role) => {
    return role?.replace(/_/g, " ")?.replace(/\b\w/g, l => l.toUpperCase()) || "User";
  };

  return (
    <div data-testid="profile-settings" className="fade-up max-w-4xl">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Account</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit']">Profile Settings</h1>
      <p className="text-base text-slate-500 dark:text-slate-400 mt-2">Manage your account settings and preferences</p>

      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      {/* Profile Information Card */}
      <Card className="card-modern mb-6" data-testid="profile-info-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00D4C8] to-[#39D98A] flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Profile Information</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Update your personal details</p>
            </div>
          </div>

          {/* User Avatar and Info */}
          <div className="flex items-center gap-4 mb-6 p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00D4C8] to-[#39D98A] flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {user?.name?.charAt(0)?.toUpperCase() || "U"}
            </div>
            <div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">{user?.name || "User"}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">@{user?.username}</p>
              <Badge className={`mt-1 text-xs ${getRoleBadgeColor(user?.role)}`}>
                {formatRole(user?.role)}
              </Badge>
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    className="pl-10 h-11 rounded-xl"
                    placeholder="Enter your name"
                    data-testid="profile-name-input"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    type="email"
                    value={email} 
                    onChange={(e) => setEmail(e.target.value)} 
                    className="pl-10 h-11 rounded-xl"
                    placeholder="Enter your email"
                    data-testid="profile-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Username</label>
                <div className="relative">
                  <Shield className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={user?.username || ""} 
                    disabled
                    className="pl-10 h-11 rounded-xl bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Username cannot be changed</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Member Since</label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"} 
                    disabled
                    className="pl-10 h-11 rounded-xl bg-slate-50 dark:bg-slate-800 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>

            {profileError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {profileError}
              </div>
            )}

            {profileSuccess && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm">
                <Check className="w-4 h-4" />
                Profile updated successfully!
              </div>
            )}

            <Button 
              type="submit" 
              className="h-11 rounded-xl bg-gradient-to-r from-[#00D4C8] to-[#39D98A] hover:from-[#00B4AA] hover:to-[#2FC77A] text-white font-semibold shadow-md btn-press"
              disabled={profileLoading}
              data-testid="save-profile-btn"
            >
              {profileLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card className="card-modern mb-6" data-testid="password-change-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center">
              <Lock className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Change Password</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Update your password to keep your account secure</p>
            </div>
          </div>

          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword} 
                  onChange={(e) => setCurrentPassword(e.target.value)} 
                  className="pl-10 pr-10 h-11 rounded-xl"
                  placeholder="Enter current password"
                  data-testid="current-password-input"
                />
                <button 
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                >
                  {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    className="pl-10 pr-10 h-11 rounded-xl"
                    placeholder="Enter new password"
                    data-testid="new-password-input"
                  />
                  <button 
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-2 block uppercase tracking-wider">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input 
                    type="password"
                    value={confirmPassword} 
                    onChange={(e) => setConfirmPassword(e.target.value)} 
                    className="pl-10 h-11 rounded-xl"
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
            </div>

            {passwordError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 text-sm">
                <Check className="w-4 h-4" />
                Password changed successfully!
              </div>
            )}

            <Button 
              type="submit" 
              className="h-11 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-md btn-press"
              disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
              data-testid="change-password-btn"
            >
              {passwordLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Preferences Card */}
      <Card className="card-modern" data-testid="preferences-card">
        <CardContent className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-purple-500 flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Preferences</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Customize your experience</p>
            </div>
            {prefsSuccess && (
              <Badge className="ml-auto bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 border-0">
                <Check className="w-3 h-3 mr-1" /> Saved
              </Badge>
            )}
          </div>

          {/* Theme Selection */}
          <div className="mb-6">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3 block uppercase tracking-wider flex items-center gap-2">
              <Palette className="w-4 h-4" /> Theme
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { value: "light", icon: Sun, label: "Light" },
                { value: "dark", icon: Moon, label: "Dark" },
                { value: "system", icon: Monitor, label: "System" }
              ].map(({ value, icon: Icon, label }) => (
                <button
                  key={value}
                  onClick={() => handleUpdatePreferences("theme", value)}
                  className={`p-4 rounded-xl border-2 transition-all btn-press flex flex-col items-center gap-2 ${
                    preferences.theme === value 
                      ? "border-[#00D4C8] bg-[#00D4C8]/10 dark:bg-[#00D4C8]/20" 
                      : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                  }`}
                  data-testid={`theme-${value}-btn`}
                >
                  <Icon className={`w-6 h-6 ${preferences.theme === value ? "text-[#00D4C8]" : "text-slate-400"}`} />
                  <span className={`text-sm font-medium ${preferences.theme === value ? "text-[#00D4C8]" : "text-slate-600 dark:text-slate-300"}`}>
                    {label}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <Separator className="my-6 bg-slate-200 dark:bg-slate-700" />

          {/* Notification Preferences */}
          <div className="space-y-4">
            <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-3 block uppercase tracking-wider flex items-center gap-2">
              <Bell className="w-4 h-4" /> Notifications
            </label>
            
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">In-App Notifications</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Receive notifications within the app</p>
              </div>
              <Switch 
                checked={preferences.notifications_enabled}
                onCheckedChange={(checked) => handleUpdatePreferences("notifications_enabled", checked)}
                data-testid="notifications-toggle"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Email Notifications</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Receive important updates via email</p>
              </div>
              <Switch 
                checked={preferences.email_notifications}
                onCheckedChange={(checked) => handleUpdatePreferences("email_notifications", checked)}
                data-testid="email-notifications-toggle"
              />
            </div>

            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
              <div>
                <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Compact View</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Use a more condensed layout</p>
              </div>
              <Switch 
                checked={preferences.compact_view}
                onCheckedChange={(checked) => handleUpdatePreferences("compact_view", checked)}
                data-testid="compact-view-toggle"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin: Notification Rules */}
      {user?.role === "admin" && (
        <div className="mt-6">
          <NotificationRulesSection />
        </div>
      )}
    </div>
  );
};
